export function numericCell(value) {
  if (typeof value === "boolean" || value == null) return null
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  const text = String(value).replace(/[\s,]/g, "")
  if (!text) return null
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

export function formattedCell(value, config) {
  if (!config || config.format === "auto" || config.format === "text") return String(value ?? "")
  const number = numericCell(value)
  if (number == null) return String(value ?? "")
  const decimals = config.format === "integer" ? 0 : Math.max(0, Math.min(6, Number(config.decimals ?? 2) || 0))
  const result = number.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return config.format === "percent" ? `${result}%` : result
}

export function tableSummary(rows, headers, operations = {}) {
  return Object.fromEntries(
    headers.map((header) => {
      const operation = operations[header] || "none"
      if (operation === "none") return [header, ""]
      if (operation === "count") return [header, rows.length]
      const numbers = rows.map((row) => numericCell(row[header])).filter((number) => number !== null)
      if (!numbers.length) return [header, ""]
      if (operation === "sum") return [header, numbers.reduce((sum, number) => sum + number, 0)]
      if (operation === "avg") return [header, numbers.reduce((sum, number) => sum + number, 0) / numbers.length]
      if (operation === "min") return [header, numbers.reduce((min, number) => Math.min(min, number), Infinity)]
      if (operation === "max") return [header, numbers.reduce((max, number) => Math.max(max, number), -Infinity)]
      return [header, ""]
    }),
  )
}

export function chartItems(rows, data, type, labels) {
  const measures =
    data?.measures?.length && ["bar", "line", "multiSeries"].includes(type)
      ? data.measures
      : [data?.metric || (data?.aggregation === "count" ? "__count__" : "")].filter(Boolean)
  if (type === "scatter") {
    const x = data?.xColumn
    const y = data?.yColumn
    if (!x || !y || x === y) return []
    return rows.flatMap((row) => {
      const xValue = numericCell(row[x])
      const yValue = numericCell(row[y])
      const size = data?.sizeColumn ? numericCell(row[data.sizeColumn]) : null
      if (xValue == null || yValue == null || (data?.sizeColumn && size == null)) return []
      return [{ name: String(row[data?.label] ?? ""), value: yValue, x: xValue, size }]
    })
  }
  if (!data?.label || !rows.length || !measures.length) return []
  const rankedMeasures = data?.topBy && !measures.includes(data.topBy) ? [...measures, data.topBy] : measures
  // A category's rank is determined by its aggregate, never by the first row encountered.
  const groups = new Map()
  rows.forEach((row) => {
    const name = String(row[data.label] ?? labels.itemLabel.replace("{count}", String(groups.size + 1)))
    if (!groups.has(name)) groups.set(name, Object.fromEntries(rankedMeasures.map((measure) => [measure, []])))
    rankedMeasures.forEach((measure) => {
      const number = numericCell(row[measure])
      if (data?.aggregation === "count") groups.get(name)[measure].push(1)
      else if (number != null) groups.get(name)[measure].push(number)
    })
  })
  const aggregate = (values) => {
    if (data?.aggregation === "count") return values.length
    if (!values.length) return 0
    if (data?.aggregation === "avg") return values.reduce((sum, value) => sum + value, 0) / values.length
    if (data?.aggregation === "min") return values.reduce((min, value) => Math.min(min, value), Infinity)
    if (data?.aggregation === "max") return values.reduce((max, value) => Math.max(max, value), -Infinity)
    return values.reduce((sum, value) => sum + value, 0)
  }
  const items = [...groups].map(([name, values]) => ({
    name,
    values: Object.fromEntries(rankedMeasures.map((measure) => [measure, aggregate(values[measure])])),
    value: aggregate(values[measures[0]] || []),
  }))
  if (data?.topBy)
    items.sort((first, second) => {
      const value = (first.values[data.topBy] ?? first.value) - (second.values[data.topBy] ?? second.value)
      return data.topOrder === "asc" ? value : -value
    })
  return items.slice(0, data?.showTop || 12)
}

export function removeFreeStyleAxis(value, axis, removed) {
  const cellAlign = Object.fromEntries(
    Object.entries(value.cellAlign || {}).flatMap(([key, align]) => {
      const [row, col] = key.split(":").map(Number)
      if (!Number.isInteger(row) || !Number.isInteger(col)) return []
      if ((axis === "row" && row === removed) || (axis === "column" && col === removed)) return []
      return [
        [
          `${row - (axis === "row" && row > removed ? 1 : 0)}:${col - (axis === "column" && col > removed ? 1 : 0)}`,
          align,
        ],
      ]
    }),
  )
  return axis === "row"
    ? {
        ...value,
        rows: value.rows.filter((_, index) => index !== removed),
        rowTypes: value.rowTypes.filter((_, index) => index !== removed),
        cellAlign,
      }
    : {
        ...value,
        headers: value.headers.filter((_, index) => index !== removed),
        columnWidths: value.columnWidths.filter((_, index) => index !== removed),
        columnTypes: value.columnTypes.filter((_, index) => index !== removed),
        rows: value.rows.map((row) => row.filter((_, index) => index !== removed)),
        cellAlign,
      }
}
